import * as React from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@noor/lib/cn';

interface EmptyStateProps {
  /** Optional icon shown in a tinted chip above the title. */
  icon?: LucideIcon;
  /** Headline — what's empty / coming. */
  title: string;
  /** Supporting copy beneath the title. */
  description?: string;
  /** Optional call-to-action slot (a button, link, etc.). */
  action?: React.ReactNode;
  className?: string;
}

/**
 * Centered empty / placeholder state for blank screens, zero-result tables,
 * and "coming soon" stubs. Centralizes the placeholder pattern so every empty
 * surface looks intentional rather than unfinished. Presentational only.
 */
export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center rounded-xl border border-dashed bg-card/50 px-6 py-16 text-center',
        className,
      )}
    >
      {Icon && (
        <div className="mb-4 flex size-12 items-center justify-center rounded-xl bg-accent text-accent-foreground ring-1 ring-primary/10 ring-inset">
          <Icon className="size-6" />
        </div>
      )}
      <h3 className="text-base font-semibold text-foreground">{title}</h3>
      {description && (
        <p className="mt-1.5 max-w-md text-sm text-muted-foreground">{description}</p>
      )}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}
